import React, { useMemo } from 'react';
import { scaleLinear } from '@visx/scale';
import RangeSpan from './RangeSpan';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import cx from 'classnames';

import styles from './CapabilityResultsChart.module.scss';

const margin = {
  top: 7,
  right: 0,
  bottom: 7,
  left: 0,
};

const CapabilityResultBar = ({
  segmentWidth,
  segmentSpacing = 0,
  height,
  elementMetrics,
  capabilityElementMap,
  capability,
}) => {
  const { width, chartWidth, chartHeight } = useMemo(() => {
    const width =
      (segmentWidth + segmentSpacing) * Object.keys(elementMetrics).length -
      segmentSpacing +
      margin.left +
      margin.right;
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    return { width, chartWidth, chartHeight };
  }, [elementMetrics, segmentWidth, segmentSpacing, height]);

  const capabilityElements = Object.values(capabilityElementMap).sort(
    (a, b) => a.paletteIndex - b.paletteIndex
  );

  const scaleY = scaleLinear({
    domain: [0, 1],
    range: [chartHeight, 0],
  });

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  // If you don't want to use a Portal, simply replace `TooltipInPortal` below with
  // `Tooltip` or `TooltipWithBounds` and remove `containerRef`
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    // use TooltipWithBounds
    detectBounds: true,
    // when tooltip containers are scrolled, this will correctly update the Tooltip position
    scroll: true,
  });

  const handleMouseEnter = (event, data) => {
    const coords = localPoint(event.target.ownerSVGElement, event);
    showTooltip({
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
      tooltipData: data,
    });
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        ref={containerRef}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g>
            <line x1={0} y1={0} x2={chartWidth} y2={0} className="chart-axis" />
            <line
              x1={0}
              y1={chartHeight}
              x2={chartWidth}
              y2={chartHeight}
              className="chart-axis"
            />
            {/* <text x={0} y={2} className="chart-axis-label hanging">
            Max
          </text>
          <text x={0} y={chartHeight - 2} className="chart-axis-label">
            Min
          </text> */}
          </g>

          {capabilityElements.map((ce, i) => {
            const ceMetrics = elementMetrics[ce.key];
            if (!ceMetrics) {
              return null;
            }
            const { minScore, maxScore, normalizedScore } = ceMetrics;

            return (
              <g
                key={ce.key}
                transform={`translate(${
                  i * (segmentWidth + segmentSpacing)
                }, 0)`}
                className={`palette-chart-${
                  capabilityElementMap[ce.key].paletteIndex
                }`}
                onMouseEnter={(e) =>
                  handleMouseEnter(e, { element: ce, metrics: ceMetrics })
                }
                onMouseLeave={handleMouseLeave}
              >
                <RangeSpan
                  x={segmentWidth / 2 - 3}
                  width={6}
                  min={scaleY(maxScore)}
                  max={scaleY(minScore)}
                  minimumDifference={10}
                  absoluteMin={0}
                  absoluteMax={chartHeight}
                />

                <g>
                  <circle
                    cx={segmentWidth / 2}
                    cy={scaleY(normalizedScore)}
                    r={6}
                    className="palette-fill"
                  />
                  <circle
                    cx={segmentWidth / 2}
                    cy={scaleY(normalizedScore)}
                    r={3}
                    className="post-hole"
                  />
                </g>
              </g>
            );
          })}
        </g>
      </svg>
      {tooltipOpen && (
        <TooltipInPortal
          // set this to random so it correctly updates with parent bounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
        >
          <div className={styles['tooltip']}>
            <div
              className={cx(styles['tooltip-capability'], 'line-clamp-var')}
              style={{ '--line-clamp': 5 }}
            >
              {capability.name}
            </div>
            <div
              className={cx(
                `palette-chart-${
                  capabilityElementMap[tooltipData.element.key].paletteIndex
                }`
              )}
            >
              <span className="palette-text">{tooltipData.element.name}</span>
            </div>
            <div className={styles['tooltip-data-points']}>
              <div>Mean:</div>
              <div>
                {tooltipData.metrics.normalizedScore != null
                  ? `${Math.round(
                      tooltipData.metrics.normalizedScore * 100
                    )}/100`
                  : 'No data'}
              </div>
              <div>Min:</div>
              <div>
                {tooltipData.metrics.normalizedScore != null
                  ? `${Math.round(tooltipData.metrics.minScore * 100)}/100`
                  : 'No data'}
              </div>
              <div>Max:</div>
              <div>
                {tooltipData.metrics.normalizedScore != null
                  ? `${Math.round(tooltipData.metrics.maxScore * 100)}/100`
                  : 'No data'}
              </div>
            </div>
          </div>
          {/* {JSON.stringify(tooltipData)} */}
        </TooltipInPortal>
      )}
    </>
  );
};

export default CapabilityResultBar;
